import Button from '@allesauto/core/button';
import SearchDropdown, { type Option } from '@allesauto/core/searchDropdown';
import TextLink from '@allesauto/core/textLink';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import translate from '@allesauto/i18n';
import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import { Routes_DE as Routes } from '@allesauto/utils/consts';
import { toUrlJSON } from '@allesauto/utils/funcs';
import { FC, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useServiceCategories } from '@/global/hooks/useServiceCategories';

const ServiceBookingForm: FC = () => {
  const intl = useIntl();
  const whiteLabel = useWhiteLabel();
  const [serviceOptions, setServiceOptions] = useState<Option[]>();
  const [service, setService] = useState<Option | readonly Option[] | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const { serviceCategories, isLoading: serviceCategoriesLoading } =
    useServiceCategories();

  const className = {
    label: 'flex flex-col mb-[6px]',
    dropdown: 'mb-[134px] w-full',
    button: 'mb-25px',
    textLink: 'flex items-center justify-center',
  };

  const getSelectedService = useCallback(
    (sku: string) => {
      const getSelectedService =
        serviceCategories &&
        serviceCategories
          .map((category) => {
            return category?.services?.find((service) => service?.sku === sku);
          })
          .sort();

      return getSelectedService && getSelectedService[0];
    },
    [serviceCategories]
  );

  const handleSubmit = useCallback(() => {
    localStorage.removeItem('serviceBooking');
    const url = new URL(window.location.href);
    const currentService = service ? (service as Option) : null;
    const getService = getSelectedService(
      currentService ? currentService.value.toString() : ''
    );
    setLoading(true);
    if (getService) {
      url.pathname = Routes.SERVICE_BOOKING;
      const {
        sku,
        description = '',
        title = '',
        abstractProductSku = '',
      } = getService;
      const v = toUrlJSON({ sku, description, title, abstractProductSku });
      if (typeof v === 'string') {
        url.searchParams.set('service', v);
      }
    } else {
      url.pathname = Routes.WORKSHOP_SERVICES;
    }

    const heroAction = getService?.title.toLocaleLowerCase();

    pushAnalyticsEvent({
      event: 'hero stage',
      hero_category: 'service',
      hero_action: heroAction,
    });

    window.open(url.toString(), '_top'); // break out of iFrame in WhiteLabel QuickEntry form
  }, [getSelectedService, service]);

  useEffect(() => {
    const createServiceOptions: Option[] = [];
    serviceCategories &&
      serviceCategories.map((serviceBooking) => {
        serviceBooking?.services &&
          serviceBooking.services.map((service) => {
            createServiceOptions.push({
              value: service?.sku || '',
              label: service?.title || '',
            });
          });
      });
    setServiceOptions(createServiceOptions);
  }, [serviceCategories]);

  return (
    <>
      <div className={className.dropdown}>
        <SearchDropdown
          isMulti={false}
          options={serviceOptions}
          placeholder={translate(
            intl,
            'ui.QUICK_ENTRY_WIDGET.SERVICE_FORM.DROPDOWN_PLACEHOLDER'
          )}
          value={service}
          onChange={(v) => setService(v as Option)}
          context="service-booking-form"
          darkBg={false}
          headline={translate(
            intl,
            'ui.QUICK_ENTRY_WIDGET.SERVICE_FORM.QUESTION_LABEL'
          )}
          isLoading={serviceCategoriesLoading}
        />
      </div>

      <div className={className.button}>
        <Button
          onClick={handleSubmit}
          isLoading={loading}
          fullWidth
          darkBg={false}
          whitelabel={!!whiteLabel}
        >
          {translate(intl, 'ui.QUICK_ENTRY_WIDGET.SERVICE_FORM.BUTTON_LABEL')}
        </Button>
      </div>

      <div className={className.textLink}>
        <TextLink
          text={translate(
            intl,
            'ui.QUICK_ENTRY_WIDGET.SERVICE_FORM.LINK_SERVICE_OVERVIEW'
          )}
          href={Routes.WORKSHOP_SERVICES}
        />
      </div>
    </>
  );
};

export default ServiceBookingForm;
